import type { ChartType } from '../../PageConfigProvider/types/chart';
import type {
  PlotOptions,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { extractMetricsFromText } from './text-metrics';

type HandlebarsFieldConfig = {
  chartType: ChartType;
  fields: Array<keyof PlotOptions>;
  condition?: (item: PositionedItem) => boolean;
};

const HANDLEBARS_FIELD_REGISTRY: HandlebarsFieldConfig[] = [
  {
    chartType: 'text',
    fields: ['textHtml'],
  },
  {
    chartType: 'line',
    fields: ['yAxisMin', 'yAxisMax'],
    condition: (item) =>
      !!(item?.plot?.yAxisMin?.trim() || item?.plot?.yAxisMax?.trim()),
  },
];

export const getHandlebarsFields = (
  item: PositionedItem,
  chartType: ChartType
): string[] => {
  const configs = HANDLEBARS_FIELD_REGISTRY.filter(
    (c) => c.chartType === chartType
  );

  const fromFlatFields = configs.flatMap((config) => {
    if (config.condition && !config.condition(item)) {
      return [];
    }

    return config.fields
      .map((field) => item?.plot?.[field] as string | undefined)
      .filter(
        (val): val is string => typeof val === 'string' && val.trim().length > 0
      );
  });

  const gaugeV2Fields =
    chartType === 'gauge-v2'
      ? [
          item?.plot?.gaugeV2?.value,
          item?.plot?.gaugeV2?.min,
          item?.plot?.gaugeV2?.max,
        ].filter(
          (val): val is string =>
            typeof val === 'string' && val.trim().length > 0
        )
      : [];

  return [...fromFlatFields, ...gaugeV2Fields];
};

export const extractMetricsFromHandlebarsFields = (
  item: PositionedItem,
  chartType: ChartType
): MetricValues[] => {
  const fields = getHandlebarsFields(item, chartType);
  const metrics = fields.flatMap(extractMetricsFromText);
  return Array.from(new Set(metrics)).sort();
};

/**
 * Single source of truth for when multi-metric data is needed (e.g. to drive
 * MultiMetricDataProvider). Used by chart layer to choose provider.
 */
export const needsMultiMetricData = (
  item: PositionedItem | undefined,
  chartType: ChartType | undefined
): boolean =>
  chartType === 'gauge-v2' ||
  (chartType === 'line' &&
    !!(item?.plot?.yAxisMin?.trim() || item?.plot?.yAxisMax?.trim()));
